import { OMARCHY_PALETTES } from "./palettes.generated";

/**
 * Colour themes ("palettes").
 *
 * - `clear` is the house design. It is *adaptive*: the existing Light / Dark / System preference
 *   (`User.themePreference`) decides which of its two variants shows.
 * - Omarchy palettes are *fixed*: each is one light or one dark theme, so the page takes the palette's own
 *   tone and the Light / Dark preference does not apply while one is chosen.
 *
 * A palette only changes colour tokens (src/app/palettes.generated.css). Layout, type and radii never change.
 * Docs: docs/theming.md.
 */

export type PaletteTone = "light" | "dark" | "adaptive";
export type PaletteFamily = "clear" | "omarchy";

export type PaletteSwatch = { bg: string; surface: string; ink: string; accent: string };

export type Palette = {
  id: string;
  label: string;
  tone: PaletteTone;
  family: PaletteFamily;
  note: string;
  /** Colours shown on the preview tile. For the adaptive Clear palette this is the light variant. */
  swatch: PaletteSwatch;
  /** The dark variant, for adaptive palettes only. */
  darkSwatch?: PaletteSwatch;
  /** Omarchy's terminal colours (red, yellow, green, cyan, blue, magenta), shown as dots on the tile. */
  terminal?: string[];
};

export const DEFAULT_PALETTE_ID = "clear";
export const PALETTE_COOKIE = "gg-palette";

export const CLEAR_PALETTE: Palette = {
  id: DEFAULT_PALETTE_ID,
  label: "Clear",
  tone: "adaptive",
  family: "clear",
  note: "Clean and price-first. Light, dark or system.",
  swatch: { bg: "#f4f7f5", surface: "#ffffff", ink: "#0c2218", accent: "#0a7f59" },
  darkSwatch: { bg: "#0a1410", surface: "#111d17", ink: "#e7f1eb", accent: "#34d399" },
};

export const PALETTES: Palette[] = [
  CLEAR_PALETTE,
  ...OMARCHY_PALETTES.map(
    (p): Palette => ({
      id: p.id,
      label: p.label,
      tone: p.tone,
      family: "omarchy",
      note: `Omarchy · ${p.tone}`,
      swatch: p.swatch,
      terminal: p.terminal,
    }),
  ),
];

export const PALETTE_FAMILIES: { id: PaletteFamily; label: string }[] = [
  { id: "clear", label: "Clear" },
  { id: "omarchy", label: "Omarchy" },
];

const BY_ID = new Map(PALETTES.map((p) => [p.id, p]));

export function isPaletteId(value: unknown): value is string {
  return typeof value === "string" && BY_ID.has(value);
}

/** Unknown, missing or stale ids fall back to Clear, so a removed palette can never break the page. */
export function resolvePaletteId(value: unknown): string {
  return isPaletteId(value) ? value : DEFAULT_PALETTE_ID;
}

export function getPalette(value: unknown): Palette {
  return BY_ID.get(resolvePaletteId(value)) ?? CLEAR_PALETTE;
}

/** The tone a fixed palette forces, or null for the adaptive Clear palette. */
export function fixedTone(value: unknown): "light" | "dark" | null {
  const tone = getPalette(value).tone;
  return tone === "adaptive" ? null : tone;
}

/** Page background for the browser's address-bar colour. */
export function paletteThemeColor(value: unknown, mode: "light" | "dark"): string {
  const palette = getPalette(value);
  if (palette.tone === "adaptive") {
    return mode === "dark" ? (palette.darkSwatch ?? palette.swatch).bg : palette.swatch.bg;
  }
  return palette.swatch.bg;
}
